import React, {PureComponent, useState, useCallback, useEffect} from 'react';

import {toMap} from '../../lib/helpers';
import {DATE_RANGE_OPTIONS, MILLIS_PER_HOUR} from '../../lib/timeline';
import DateTimeInput from '../../components/DateTimeInput';
import Button from '../../components/Button';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Input from '../../components/Input';

import Filter from './Filter';
import {isQuestionDeleted} from "../FormBuilderScreen/shared";

class SheetRowFilter extends PureComponent {
  state = {
    filters: this.props.filters ? this.props.filters.filters : [],
    validations: {},
    minDate: this.props.filters ? this.props.filters.minDate : null,
    maxDate: this.props.filters ? this.props.filters.maxDate : null,
    reviewState: this.props.filters ? this.props.filters.reviewState : '',
    range: null
  };

  handleFilterChange = filter => {
    this.setState(prevState => ({
      filters: [
        ...prevState.filters.filter(f => f.questionId !== filter.questionId),
        filter
      ].filter(f => f.operator !== null),
      validations: Object.keys(prevState.validations).filter(key => key !== filter.questionId).reduce((map, key) => {
        map[key] = prevState.validations[key];
        return map;
      }, {})
    }));
  }

  handleApplyFilters = () => {
    const {template, onApplyFilters} = this.props;
    let {filters, minDate, maxDate, range, reviewState} = this.state;
    if (range && range > 0) {
      const now = new Date();
      minDate = new Date(now.getTime() - range);
      maxDate = now;
    }
    const questionsById = toMap([...template.contextQuestions, ...template.questions], question => question.id);
    const validations = filters.reduce((map, filter) => {
      const messages = Filter.validate(questionsById[filter.questionId], filter);

      if (messages.length > 0) {
        map[filter.questionId] = messages;
      }

      return map;
    }, {});

    if ((minDate && !maxDate) || (!minDate && maxDate)) {
      validations['row_date_range'] = ['Please enter a start date and end date'];
    }

    this.setState({validations});

    if (Object.keys(validations).length > 0) {
      // There was validation issues, so we won't apply any filters.
      return;
    }

    onApplyFilters({and: true, minDate, maxDate, reviewState, filters});
  }

  handleClearFilters = () => {
    this.setState({
      minDate: null,
      maxDate: null,
      range: null,
      reviewState: '',
      filters: [],
      validations: {}
    });
  }

  render() {
    const {template, catalog, open, onClose, showDeleted} = this.props;
    const {filters, validations, minDate, maxDate, reviewState} = this.state;
    const filtersByQuestionId = toMap(filters, filter => filter.questionId);
    const questions = [...template.contextQuestions].sort((a, b) => a.order - b.order)
      .concat([...template.questions].sort((a, b) => a.order - b.order))
      .filter(question => question.type !== 'IMAGE' && question.type !== 'SIGNATURE');

    return (
      <Form onSubmit={this.handleApplyFilters}>
        <SideModal
          title="Filter Data"
          open={open}
          onClose={onClose}
          options={[
            <Button type="submit">Apply Filters</Button>,
            <Button secondary onClick={this.handleClearFilters}>Clear Filters</Button>
          ]}>
          <SideModal.Content>
            <Form.Group>
              <DateRangeFilter
                minDate={minDate}
                maxDate={maxDate}
                onChange={(minDate, maxDate, range) => this.setState({minDate, maxDate, range})}
                validations={validations['row_date_range']}
              />
            </Form.Group>
            {template.reviewers.length > 0 && (
              <Form.Group>
                <Input
                  id="filter_review_state"
                  label="Review Status"
                  type="select"
                  value={reviewState}
                  onChange={e => this.setState({reviewState: e.target.value})}>
                  <option value="">All</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="PENDING">Pending</option>
                </Input>
              </Form.Group>
            )}
            {(!showDeleted ? questions.filter(question => !isQuestionDeleted(question)) : questions).map(question => (
              <Form.Group key={question.id}>
                <Filter
                  question={question}
                  catalog={catalog}
                  filter={filtersByQuestionId[question.id] || emptyFilter(question)}
                  onChange={this.handleFilterChange}
                  validations={validations[question.id] || []}
                />
              </Form.Group>
            ))}
          </SideModal.Content>
        </SideModal>
      </Form>
    );
  }
}

function getInitialRange(minDate, maxDate) {
  if (!minDate || !maxDate) {
    return DATE_RANGE_OPTIONS[0].range;
  }
  const diff = maxDate.getTime() - minDate.getTime();
  const match = DATE_RANGE_OPTIONS.find(option => option.range > 0 && Math.abs(option.range - diff) < MILLIS_PER_HOUR);
  return match ? match.range : -1;
}

function DateRangeFilter({minDate, maxDate, onChange, validations}) {
  const [selectedRange, setSelectedRange] = useState(() => getInitialRange(minDate, maxDate));

  useEffect(() => {
    if (!minDate && !maxDate) {
      const now = new Date();
      onChange(new Date(now.getTime() - DATE_RANGE_OPTIONS[0].range), now, DATE_RANGE_OPTIONS[0].range);
    }
  }, []);

  const handleRangeChange = useCallback((e) => {
    const range = Number(e.target.value);
    setSelectedRange(range);
    if (range > 0) {
      const now = new Date();
      onChange(new Date(now.getTime() - range), now, range);
    } else {
      onChange(null, null, null);
    }
  }, [onChange]);

  return (
    <>
      <Input
        id="filter_date_range"
        type="select"
        label="Date Range"
        value={selectedRange}
        onChange={handleRangeChange}
      >
        {DATE_RANGE_OPTIONS.map(option => (
          <option key={option.range} value={option.range}>{option.label}</option>
        ))}
      </Input>
      {selectedRange === -1 && (
        <>
          <DateTimeInput
            id="filter_start_date"
            placeholder="Start Date"
            style={{marginTop: '0.5rem'}}
            date={minDate}
            onChange={minDate => onChange(minDate, maxDate)}
            invalid={validations}
          />
          <DateTimeInput
            placeholder="End Date"
            style={{marginTop: '0.5rem'}}
            date={maxDate}
            onChange={maxDate => onChange(minDate, maxDate)}
            invalid={validations}
            validationText={validations && validations[0]}
          />
        </>
      )}
    </>
  );
}

function emptyFilter(question) {
  return {
    questionId: question.id,
    operator: null,
    values: []
  };
}

export default SheetRowFilter;

